// filepath: apps/web/src/features/admin/effectsMetrics/CircuitTable.tsx
//
// FR-UI-ADM-9 AC-2 / AC-3 — per-endpoint circuit-breaker table.
//
// Renders one row per endpoint key with:
//   - state (Closed/Open/Half-Open) with colour indicator + non-colour
//     fallback (text label + icon) so colour-blind users get the
//     same information (WCAG 1.4.1)
//   - opened_at_unix_ms → "time in current state" (or "—" for CLOSED)
//   - consecutive failures
//   - "Configure" button → opens CircuitBreakerConfigDialog
//
// Endpoint keys are rendered as text nodes only (INV-9).

import React from "react";
import { useTranslation } from "react-i18next";
import type { CircuitEndpoint, CircuitSnapshotResponse } from "./types";

interface CircuitTableProps {
  snapshot: CircuitSnapshotResponse | undefined;
  onConfigure: (endpointKey: string, endpoint: CircuitEndpoint) => void;
}

const STATE_LABELS = {
  closed: "Closed",
  open: "Open",
  half_open: "Half-Open",
} as const;

function timeInState(openedAtUnixMs: number): string {
  if (openedAtUnixMs <= 0) return "—";
  const elapsedMs = Date.now() - openedAtUnixMs;
  if (elapsedMs < 0) return "—";
  const s = Math.floor(elapsedMs / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h${m % 60}m`;
}

export function CircuitTable({
  snapshot,
  onConfigure,
}: CircuitTableProps): React.ReactElement {
  const { t } = useTranslation();
  const endpoints = Object.entries(snapshot?.endpoints ?? {});

  if (endpoints.length === 0) {
    return (
      <p data-testid="no-circuit-endpoints" className="effects-circuit-empty">
        {t(
          "admin.effects.metrics.no_endpoints",
          "No circuit-breaker endpoints configured.",
        )}
      </p>
    );
  }

  return (
    <table
      className="effects-circuit-table"
      aria-label={t(
        "admin.effects.metrics.circuit_table_label",
        "Circuit breaker state",
      )}
    >
      <thead>
        <tr>
          <th scope="col">
            {t("admin.effects.metrics.column.endpoint", "Endpoint")}
          </th>
          <th scope="col">
            {t("admin.effects.metrics.column.state", "State")}
          </th>
          <th scope="col">
            {t("admin.effects.metrics.column.time_in_state", "Time in state")}
          </th>
          <th scope="col">
            {t(
              "admin.effects.metrics.column.failures_5min",
              "Failures observed (5 min)",
            )}
          </th>
          <th scope="col">
            {t(
              "admin.effects.metrics.column.consecutive_failures",
              "Consecutive failures",
            )}
          </th>
          <th scope="col">
            {t("admin.effects.metrics.column.action", "Action")}
          </th>
        </tr>
      </thead>
      <tbody>
        {endpoints.map(([ek, snap]) => (
          <tr key={ek}>
            <td>{ek}</td>
            <td>
              <span
                className={`circuit-state circuit-state-${snap.state}`}
                aria-label={`Circuit state: ${STATE_LABELS[snap.state]}`}
                data-state={snap.state}
              >
                <span aria-hidden="true" className="circuit-state-dot" />
                {STATE_LABELS[snap.state]}
              </span>
            </td>
            <td>{timeInState(snap.opened_at_unix_ms)}</td>
            <td>—</td>
            <td>{snap.consecutive_failures}</td>
            <td>
              <button
                type="button"
                onClick={() => onConfigure(ek, snap)}
                aria-label={t(
                  "admin.effects.metrics.configure_endpoint",
                  "Configure breaker for {{endpoint}}",
                  { endpoint: ek },
                )}
              >
                {t("admin.effects.metrics.dialog_title", "Configure breaker")}
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
